import { useSyncExternalStore } from 'react';

/**
 * Faixa de viewport da tela atual — a FONTE ÚNICA de responsividade do app.
 *
 * POR QUE EM JAVASCRIPT E NÃO `@media` NO CSS: as telas são escritas com `style`
 * inline, e estilo inline VENCE regra de classe na especificidade. Uma media
 * query em `globals.css` só conseguiria sobrescrever `width: 220` com
 * `!important` em cada propriedade — insustentável em 18 mil linhas. Com o
 * breakpoint em JS, o mesmo objeto de estilo é montado já com o valor certo.
 *
 * Implementado com `useSyncExternalStore` e não `useState` + `useEffect`:
 *  - o primeiro render já sai com a faixa correta (o efeito só roda DEPOIS da
 *    pintura, então o layout piscava do desktop para o mobile);
 *  - `matchMedia` é exatamente o "external store" que a API espera, e o React
 *    cuida de assinar/desassinar sem corrida.
 *
 * O snapshot é uma STRING (não objeto): `useSyncExternalStore` compara com
 * `Object.is`, e devolver um objeto novo a cada leitura entraria em loop de
 * render. Os booleanos derivados são montados no hook, depois da comparação.
 */
export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Limites em px, na borda SUPERIOR de cada faixa:
 *  - `mobile`  até 640  — celular em pé; navegação vira gaveta, tabela vira card;
 *  - `tablet`  até 1024 — celular deitado e tablet; sidebar recolhida, grade de
 *    2 colunas, tabela com rolagem horizontal;
 *  - `desktop` acima de 1024 — layout original, intocado.
 *
 * 1024 não é arbitrário: a tabela do inventário tem 9 colunas, 442px delas
 * fixas, e abaixo de ~1030px a coluna do nome da aplicação era comprimida a
 * ponto de o nome desaparecer em reticências (medido no navegador).
 */
export const BREAKPOINT_MAX = { mobile: 640, tablet: 1024 } as const;

const QUERIES = {
  mobile: `(max-width: ${BREAKPOINT_MAX.mobile}px)`,
  tablet: `(max-width: ${BREAKPOINT_MAX.tablet}px)`,
} as const;

/**
 * Forma MÍNIMA do que este hook usa de uma MediaQueryList — declarada aqui em
 * vez de usar os tipos de DOM porque o arquivo também é compilado pelo programa
 * do `apps/api` (o teste `web-hooks.test.ts` o importa), e lá `lib.dom` não
 * entra: `window.matchMedia` dava TS2339 no `bun run typecheck`.
 */
interface MinimalMediaQueryList {
  matches: boolean;
  addEventListener?: (type: 'change', cb: () => void) => void;
  removeEventListener?: (type: 'change', cb: () => void) => void;
  /** Depreciados, mas é o que o Safari < 14 entende. */
  addListener?: (cb: () => void) => void;
  removeListener?: (cb: () => void) => void;
}

type MatchMedia = (query: string) => MinimalMediaQueryList;

/**
 * `window.matchMedia` já ligado ao `window`, ou `null` fora do navegador.
 *
 * Lê SEMPRE de `window` e nunca de `globalThis` direto: é o que permite o teste
 * trocar a implementação por um dublê, e é o que faz "ambiente sem DOM" (Bun,
 * SSR) devolver `null` em vez de achar um `matchMedia` de outro lugar.
 */
function getMatchMedia(): MatchMedia | null {
  const janela = (globalThis as { window?: { matchMedia?: MatchMedia } }).window;
  const fn = janela?.matchMedia;
  return typeof fn === 'function' ? fn.bind(janela) : null;
}

function read(): Breakpoint {
  const mm = getMatchMedia();
  if (!mm) return 'desktop';
  if (mm(QUERIES.mobile).matches) return 'mobile';
  if (mm(QUERIES.tablet).matches) return 'tablet';
  return 'desktop';
}

/**
 * Assina as DUAS consultas: uma troca de faixa sempre cruza pelo menos uma
 * delas, e reagir só à de mobile deixaria tablet↔desktop sem atualizar.
 *
 * `addListener`/`removeListener` no fallback: são os depreciados, mas é o que o
 * Safari < 14 entende — e `addEventListener` em MediaQueryList só existe a
 * partir dele. Sem o fallback, iPad antigo nunca recebe a troca de faixa.
 */
function subscribe(onChange: () => void): () => void {
  const mm = getMatchMedia();
  if (!mm) return () => undefined;
  const lists = [mm(QUERIES.mobile), mm(QUERIES.tablet)];
  for (const mql of lists) {
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else mql.addListener?.(onChange);
  }
  return () => {
    for (const mql of lists) {
      if (typeof mql.removeEventListener === 'function')
        mql.removeEventListener('change', onChange);
      else mql.removeListener?.(onChange);
    }
  };
}

export interface Viewport {
  bp: Breakpoint;
  /** Até 640px. */
  mobile: boolean;
  /** Entre 641px e 1024px. */
  tablet: boolean;
  /** Acima de 1024px — layout original. */
  desktop: boolean;
  /**
   * `mobile || tablet`. É o teste que a maioria das telas quer: "não tenho
   * largura sobrando". Evita repetir `bp !== 'desktop'` em cada estilo e deixa
   * explícito nos arquivos qual decisão é de espaço e qual é só de celular.
   */
  compact: boolean;
}

export function useViewport(): Viewport {
  // `read` serve de snapshot de servidor também: sem DOM, `supported()` é falso e
  // ele já devolve 'desktop'. Um terceiro argumento próprio (`() => 'desktop'`)
  // seria a MESMA regra escrita duas vezes — e uma função que nenhum teste
  // alcança, reprovando o piso de cobertura por arquivo.
  const bp = useSyncExternalStore(subscribe, read, read);
  return {
    bp,
    mobile: bp === 'mobile',
    tablet: bp === 'tablet',
    desktop: bp === 'desktop',
    compact: bp !== 'desktop',
  };
}

/**
 * Escolhe um valor por faixa, com `tablet` caindo para o de mobile quando não
 * declarado. Serve para não encher as telas de ternário aninhado (que, além de
 * ilegível, é o code smell S3358 que acabamos de zerar):
 *
 *     padding: byViewport(vp, { mobile: 12, desktop: 24 })
 *
 * TRÊS parâmetros de tipo, e não um só: com `<T>` único o TypeScript infere `T`
 * da PRIMEIRA propriedade e recusa faixas de tipos diferentes — o caso real é
 * comum em CSS (`{ mobile: 'auto', desktop: 86 }`, `{ mobile: '100%',
 * desktop: 300 }`), e o erro sai no ponto de uso, não aqui. Com `M`, `D` e `T`
 * cada faixa carrega o seu tipo e o retorno é a união, que é o que uma
 * propriedade de estilo aceita.
 *
 * `D = M` e `T = M` (defaults, não obrigatórios) por dois motivos: `tablet`
 * ausente não vira `unknown`, e quem passa UM argumento explícito
 * (`byViewport<string>(…)`, forma que apareceu nas telas para contornar a
 * assinatura antiga) continua compilando em vez de tomar "Expected 2-3 type
 * arguments, but got 1".
 */
export function byViewport<M, D = M, T = M>(
  vp: Pick<Viewport, 'bp'>,
  values: Readonly<{ mobile: M; tablet?: T; desktop: D }>,
): M | D | T {
  if (vp.bp === 'desktop') return values.desktop;
  if (vp.bp === 'tablet') return values.tablet ?? values.mobile;
  return values.mobile;
}
